import { useEffect, useState } from 'react';

import { getUserData } from '../constants/global';
import type { IObserverData, IObserverPosting } from '../interface/observer';
import { getMyObserverPostings, getMyObserverProfile } from '../lib/observer-api';

/**
 * The signed-in observer's postings and accreditation, fetched once per session and shared by
 * every reader (home, reports hub, layout). Observers are never in `ElectionOfficial`, so this
 * replaces `get_ElectionOfficial` for them.
 *
 * <p>Postings and profile load independently: a missing profile only costs the card its
 * organisation line, never the report context.</p>
 */
let cache: IObserverData | null = null;
/** Whose answer `cache` is: a different account in the same tab must never see it. */
let cacheOwner: string | undefined;
let inflight: Promise<IObserverData> | null = null;
const listeners = new Set<(d: IObserverData) => void>();

const LOADING: IObserverData = { status: 'loading', postings: [], credential: null };

/**
 * `me/profile` carries the observer's current posting under the same ledger-first, Confirmed-application
 * rule as `me/assignments`. It answers when `me/assignments` cannot — an api.core older than that route
 * 404s it — so a confirmed observer is never told their posting failed to load while the profile has it.
 * Null when the profile did not answer either.
 */
const postingsFromProfile = (profile: Awaited<ReturnType<typeof getMyObserverProfile>>): IObserverPosting[] | null => {
	if (!profile) return null;
	const d = profile.deployment;
	return d?.entityCode && d.electionId > 0 ? [d] : [];
};

const load = (): Promise<IObserverData> => {
	if (inflight) return inflight;
	inflight = Promise.all([getMyObserverPostings(), getMyObserverProfile()])
		.then(([postings, profile]) => {
			const fallback = postingsFromProfile(profile);
			const answered = postings ?? fallback;
			const next: IObserverData = {
				status: answered === null ? 'unavailable' : 'ready',
				postings: answered ?? [],
				credential: profile?.credential ?? null,
			};
			// An outage is not cached: the next reader retries.
			cache = next.status === 'ready' ? next : null;
			cacheOwner = cache ? owner() : undefined;
			listeners.forEach((l) => l(next));
			return next;
		})
		.finally(() => {
			inflight = null;
		});
	return inflight;
};

const owner = () => getUserData().user?.UserId?.toString();

const cached = (): IObserverData | null => (cache && cacheOwner === owner() ? cache : null);

/** Drop the cached answer (logout, or after a posting change). */
export const resetObserverData = () => {
	cache = null;
};

export const useObserverData = (enabled = true): IObserverData & { retry: () => void } => {
	const [data, setData] = useState<IObserverData>(cached() ?? LOADING);

	useEffect(() => {
		if (!enabled) return undefined;
		listeners.add(setData);
		const hit = cached();
		if (hit) setData(hit);
		else void load();
		return () => {
			listeners.delete(setData);
		};
	}, [enabled]);

	return {
		...data,
		retry: () => {
			setData(LOADING);
			void load();
		},
	};
};
